import { useMemo, useState } from "react";
import { Wallet, RefreshCw, Plus, Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExpenseStatusBadge } from "@/components/expenses/ExpenseStatusBadge";
import { MarkExpensePaidDialog } from "@/components/expenses/MarkExpensePaidDialog";
import { ExpenseFormDialog } from "@/components/expenses/ExpenseFormDialog";
import { useExpenseCategories } from "@/hooks/useExpenseCategories";
import { useSuppliers } from "@/hooks/useSuppliers";
import { useExpenses, type ExpenseOrigin, type ExpenseStatus, type ExpenseRow } from "@/hooks/useExpenses";

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function isOverdue(expense: ExpenseRow) {
  if (expense.status !== "open") return false;
  if (!expense.due_date) return false;
  const due = new Date(expense.due_date + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}

export default function DespesasPage() {
  const [status, setStatus] = useState<ExpenseStatus | "all">("all");
  const [origin, setOrigin] = useState<ExpenseOrigin | "all">("all");
  const [categoryId, setCategoryId] = useState<string | "all">("all");
  const [supplierId, setSupplierId] = useState<string | "all">("all");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const filters = useMemo(
    () => ({
      status,
      origin,
      categoryId,
      supplierId,
      startDate: startDate ? new Date(startDate + "T00:00:00") : undefined,
      endDate: endDate ? new Date(endDate + "T00:00:00") : undefined,
    }),
    [status, origin, categoryId, supplierId, startDate, endDate]
  );

  const { categories } = useExpenseCategories();
  const { suppliers } = useSuppliers();
  const { expenses, isLoading, totals, refetch, markPaid, reopen, cancel, createManualExpense, updateManualExpense } = useExpenses(filters);

  const [payingExpense, setPayingExpense] = useState<ExpenseRow | null>(null);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseRow | null>(null);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Wallet className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Despesas / Contas a Pagar</h1>
            <p className="text-sm text-muted-foreground">Acompanhe status, vencimentos e origens (caixa/estoque)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => {
              setEditingExpense(null);
              setManualDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova despesa
          </Button>
          <Button variant="outline" size="sm" onClick={refetch}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Em aberto</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(totals.openAmount)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pagas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(totals.paidAmount)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="open">Aberta</SelectItem>
                  <SelectItem value="paid">Paga</SelectItem>
                  <SelectItem value="cancelled">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Origem</Label>
              <Select value={origin} onValueChange={(v) => setOrigin(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="cash_withdrawal">Sangria</SelectItem>
                  <SelectItem value="stock_entry">Entrada estoque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={categoryId} onValueChange={(v) => setCategoryId(v as any)}>
                <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fornecedor</Label>
              <Select value={supplierId} onValueChange={(v) => setSupplierId(v as any)}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Início</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Fim</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lista</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Fornecedor</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!isLoading && expenses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    Nenhuma despesa encontrada.
                  </TableCell>
                </TableRow>
              )}
              {expenses.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{new Date(e.expense_date + "T00:00:00").toLocaleDateString("pt-BR")}</TableCell>
                  <TableCell className="font-medium">{e.title}</TableCell>
                  <TableCell>{e.expense_categories?.name || "-"}</TableCell>
                  <TableCell>{e.suppliers?.name || "-"}</TableCell>
                  <TableCell>{e.due_date ? new Date(e.due_date + "T00:00:00").toLocaleDateString("pt-BR") : "-"}</TableCell>
                  <TableCell className="text-right">{formatCurrency(Number(e.amount))}</TableCell>
                  <TableCell>
                    <ExpenseStatusBadge status={e.status} isOverdue={isOverdue(e)} />
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      {e.origin === "manual" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingExpense(e);
                            setManualDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {e.status === "open" && (
                        <>
                          <Button size="sm" onClick={() => setPayingExpense(e)}>
                            Pagar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => cancel(e.id)}>
                            Cancelar
                          </Button>
                        </>
                      )}
                      {e.status === "paid" && (
                        <Button size="sm" variant="outline" onClick={() => reopen(e.id)}>
                          Reabrir
                        </Button>
                      )}
                      {e.status === "cancelled" && (
                        <Button size="sm" variant="outline" onClick={() => reopen(e.id)}>
                          Reabrir
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <MarkExpensePaidDialog
        open={!!payingExpense}
        onOpenChange={(open) => !open && setPayingExpense(null)}
        title={payingExpense?.title}
        onConfirm={async (paidAtISO) => {
          if (!payingExpense) return;
          await markPaid(payingExpense.id, paidAtISO);
          setPayingExpense(null);
        }}
      />

      <ExpenseFormDialog
        open={manualDialogOpen}
        onOpenChange={(open) => {
          setManualDialogOpen(open);
          if (!open) setEditingExpense(null);
        }}
        mode={editingExpense ? "edit" : "create"}
        expense={editingExpense}
        onSubmit={async (values) => {
          if (editingExpense) {
            await updateManualExpense(editingExpense.id, values);
          } else {
            await createManualExpense(values);
          }
        }}
      />
    </div>
  );
}
